import React from "react";
import { useNavigation, usePhoneAuth } from "../../../hooks";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import Input from "../../ui/Input";

const PhoneAuthScreen: React.FC = () => {
  const { goBack } = useNavigation();
  const {
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    timeLeft,
    isCodeSent,
    isLoading,
    error,
    sendCode,
    verifyCode,
    canResendCode,
    resetAuthFlow,
  } = usePhoneAuth();

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const formatPhoneNumber = (value: string): string => {
    const numbers = value.replace(/[^\d]/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7)
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(
      7,
      11
    )}`;
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (formatted.replace(/[^\d]/g, "").length <= 11) {
      setPhoneNumber(formatted);
    }
  };

  const handleSendCode = async () => {
    await sendCode();
  };

  const handleVerifyCode = async () => {
    const success = await verifyCode();
    if (success) {
      // verifyCode에서 이미 적절한 네비게이션 처리됨 (로그인 시 홈으로, 신규 사용자는 약관으로)
      // 기존 사용자는 useAuth의 login에서 자동으로 홈으로 이동
    }
  };

  const handleResendCode = async () => {
    await sendCode();
  };

  return (
    <div className="h-screen-dvh bg-gray-50 flex flex-col overflow-hidden">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center border-b border-gray-200 flex-shrink-0">
        <button
          onClick={goBack}
          className="flex items-center justify-center w-8 h-8 mr-3 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevronLeft" className="text-gray-600" />
        </button>
        <h1 className="text-lg font-medium text-gray-800">본인 인증</h1>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 flex flex-col px-6 py-6 overflow-y-auto min-h-0">
        {!isCodeSent ? (
          <>
            {/* 휴대폰 번호 입력 단계 */}
            <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                  휴대폰 번호를 입력해주세요
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  본인 확인을 위해 휴대폰으로 인증 코드를 보내드립니다.
                  <br />
                  인증 완료 후 자동으로 로그인 또는 회원가입이 진행됩니다.
                </p>
              </div>

              <div className="mb-6 flex flex-col items-center">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  휴대폰 번호
                </label>
                <Input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  placeholder="010-0000-0000"
                  className="text-center text-lg py-4"
                  maxLength={13}
                />
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              <Button
                fullWidth
                size="lg"
                onClick={handleSendCode}
                disabled={
                  isLoading || phoneNumber.replace(/[^\d]/g, "").length !== 11
                }
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 py-4 text-base"
              >
                {isLoading ? "코드 전송 중..." : "인증 코드 받기"}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* 인증 코드 입력 단계 */}
            <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                  인증 코드를 입력해주세요
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  <span className="font-medium text-gray-800">
                    {phoneNumber}
                  </span>
                  로 전송된
                  <br />
                  6자리 인증 코드를 입력해주세요.
                </p>
              </div>

              <div className="mb-6 flex flex-col items-center">
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                  인증 코드
                </label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(
                      e.target.value.replace(/[^\d]/g, "").slice(0, 6)
                    )
                  }
                  placeholder="000000"
                  className="text-center text-2xl font-mono tracking-widest py-4"
                  maxLength={6}
                />
              </div>

              {/* 타이머 */}
              <div className="mb-6 text-center">
                {timeLeft > 0 ? (
                  <div className="inline-flex items-center px-3 py-2 bg-blue-50 rounded-full">
                    <p className="text-blue-600 font-medium text-sm">
                      남은 시간: {formatTime(timeLeft)}
                    </p>
                  </div>
                ) : (
                  <div className="inline-flex items-center px-3 py-2 bg-red-50 rounded-full">
                    <p className="text-red-600 font-medium text-sm">
                      인증 시간이 만료되었습니다.
                    </p>
                  </div>
                )}
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  fullWidth
                  size="lg"
                  onClick={handleVerifyCode}
                  disabled={
                    isLoading || verificationCode.length !== 6 || timeLeft <= 0
                  }
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 py-4 text-base"
                >
                  {isLoading ? "인증 확인 중..." : "인증 완료"}
                </Button>

                {canResendCode && (
                  <Button
                    fullWidth
                    variant="outline"
                    size="lg"
                    onClick={handleResendCode}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 py-4"
                  >
                    인증 코드 재전송
                  </Button>
                )}
              </div>

              {/* 휴대폰 번호 변경 옵션 */}
              <div className="text-center mt-8">
                <button
                  onClick={resetAuthFlow}
                  className="text-gray-500 text-sm underline hover:text-gray-700 transition-colors"
                >
                  휴대폰 번호 변경
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PhoneAuthScreen;
